import { Link } from "react-router-dom";
import { ArrowRight, Home as HomeIcon } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import CheckList from "@/components/CheckList";
import CtaBand from "@/components/CtaBand";
import TrustBar from "@/components/TrustBar";
import { residentialServices, residentialProblems, images, business } from "@/data/site";

const schema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Residential Roofing",
  provider: { "@type": "RoofingContractor", name: business.name, telephone: "+1-215-806-5038" },
  areaServed: "Bucks County, Pennsylvania",
};

export default function Residential() {
  return (
    <>
      <Seo
        title="Residential Roofing in Bucks County, PA | Future Roofing and Repair"
        description="Residential roofing in Bucks County, PA: roof installation, repair, shingle, metal, slate & flat roofing, leak detection, gutters, siding & more. Call 215-806-5038."
        keywords="Residential roofing Bucks County, Roof repair Bucks County, Shingle roofing, Roof leak repair, Metal roofing, Siding Bucks County"
        schema={schema}
      />
      <PageHero
        kicker="Residential Roofing"
        title="Roofing for Every Home in Bucks County"
        subtitle="We work on all types of homes with quality materials and honest, dependable workmanship."
        image={images.residential}
      />
      <TrustBar />

      <section className="bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <SectionHeading kicker="What We Solve" title="Common Roofing Problems We Fix" />
            <p className="text-charcoal/80 leading-relaxed mb-6">
              Future Roofing and Repair works on all types of homes and commonly solves issues like:
            </p>
            <CheckList items={residentialProblems} columns={1} />
            <div className="mt-8 bg-cream-200 border border-charcoal/20 p-6">
              <div className="flex items-center gap-3 mb-2">
                <HomeIcon className="w-6 h-6 text-rust" />
                <h3 className="font-heading text-2xl uppercase text-charcoal">All Roof Types</h3>
              </div>
              <p className="text-charcoal/75 leading-relaxed">
                Asphalt shingle, metal, flat, and slate — we help you choose the right system for your
                home, budget, and curb appeal. Free estimates available; inspections may have a fee.
              </p>
            </div>
          </div>
          <div className="bg-cream border border-charcoal/20 shadow-md p-8">
            <SectionHeading kicker="Full Service List" title="Residential Services" />
            <CheckList items={residentialServices} columns={2} />
          </div>
        </div>
      </section>

      <CtaBand
        kicker="Quality You Can Trust"
        title="Roofing Done Right the First Time"
        text="13 years of hands-on experience on homes across Bucks County. Call us or request a free estimate."
      />

      <section className="bg-cream-200 border-y border-charcoal/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <SectionHeading
            kicker="Get Started"
            title="Request a Free Residential Estimate"
            subtitle="Tell us what's going on with your roof and we'll get back to you quickly."
            center
          />
          <Link
            to="/contact"
            data-testid="residential-estimate-cta"
            className="inline-flex items-center gap-2 bg-rust text-cream border border-charcoal/20 shadow-md hover:-translate-y-1 transition-all font-heading text-2xl uppercase px-8 py-4"
          >
            Request an Estimate <ArrowRight className="w-6 h-6" />
          </Link>
        </div>
      </section>
    </>
  );
}
